import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Steps } from "./Steps";

const Container = styled.div`
  max-width: 100%; // 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  font-family: "Manrope", sans-serif;
  box-sizing: border-box;
`;

const Section = styled(motion.section)`
  margin-bottom: 5rem;
`;

const HeroSection = styled(Section)`
  text-align: center;
`;

const ImageGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 4rem;
  }
`;

interface ContentColProps {
  $order?: string;
}

const ContentCol = styled.div<ContentColProps>`
  flex: 1;
  order: ${(props) => props.$order || "unset"};

  @media (min-width: 768px) {
    order: unset;
  }
`;

const SectionLabel = styled.div`
  color: #5575f7;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #eff2ff;
  padding: 10px;
  width: 140px;
  border-radius: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #102a8e;
  margin-bottom: 1rem;
  font-weight: bold;
`;

const Text = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Guide = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <Container>
      <HeroSection>
        <ImageGrid
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <Image
            src="/assets/grid2.png"
            alt="Healthcare professionals collaborating"
            variants={fadeIn}
          />
        </ImageGrid>
      </HeroSection>

      <Section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
      >
        <FlexContainer>
          <ContentCol>
            <Image
              src="/assets/about1.png"
              alt="Healthcare professional"
              variants={fadeIn}
            />
          </ContentCol>
          <ContentCol>
            <SectionLabel>HCPC OVERVIEW</SectionLabel>
            <SectionTitle>
              Thinking about registering with HCPC UK?
            </SectionTitle>
            <Text>
              The Health and Care Professions Council (HCPC) is the regulatory
              body for healthcare professionals in the United Kingdom. If you
              are a healthcare professional looking to practice in the UK,
              getting registered with HCPC is a crucial step to ensure your
              qualifications and competency are recognized. In this step-by-step
              guide, we will walk you through the process of registering with
              HCPC in a manifold writing style, providing clear instructions and
              helpful tips along the way. 
            </Text>
          </ContentCol>
        </FlexContainer>
      </Section>
      <Steps />
    </Container>
  );
};
